import React from 'react';
import { AlertTriangleIcon, CreditCardIcon, FileCheck2Icon, ListChecksIcon, UploadIcon } from 'lucide-react';
import type { CitizenScreen } from '../../types/navigation';
import { useStore } from '../../contexts/StoreContext';
import { MobileHeader } from '../../components/mobile/MobileHeader';
import { ProcessTracker } from '../../components/shared/ProcessTracker';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { categoryLabel, isPaid, stageInfo } from '../../utils/caseStatus';
import { daysBetween, fmtDate, nowIso, bn, taka } from '../../utils/bn';
export function CitizenCase({
  pcr,
  go
}: {
  pcr: string;
  go: (s: CitizenScreen) => void;
}) {
  const {
    getCase,
    config
  } = useStore();
  const c = getCase(pcr);
  if (!c) return <p className="p-6" data-id="element-1541" data-mp="372hyek6pr7uu">কেস পাওয়া যায়নি।</p>;
  const s = stageInfo(c);
  const daysLeft = daysBetween(nowIso(), c.deadline);
  const correction = c.documents.filter(d => d.status === 'correction');
  return <>
      <MobileHeader appName="ডিএমপি যানবাহন সেবা" title="আটককৃত যানবাহনের তথ্য" onBack={() => go({
      name: 'home'
    })} data-id="element-1542" data-mp="l8xvihxkwmd6" />
      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-3" data-id="element-1543" data-mp="g9unqpq7s21i">
        <div className="card overflow-hidden" data-id="element-1544" data-mp="bj4j5epm437v">
          <div className="relative" data-id="element-1545" data-mp="bhtfdew62vgr">
            <img src={c.photo} alt={`${c.regNo} যানবাহনের ছবি`} className="h-36 w-full object-cover" data-id="element-1546" data-mp="6kb4vk2i486n" />
            <span className="absolute left-3 top-3 rounded-md bg-navy px-2 py-1 font-mono text-xs font-bold text-white" data-id="element-1547" data-mp="2ipxge8qkm3u">{c.pcr}</span>
          </div>
          <dl className="divide-y divide-line text-sm" data-id="element-1548" data-mp="3to7tpeycl7fo">
            {[['যানবাহন', c.regNo], ['ধরন', categoryLabel(c.category, config)], ['আটকের কারণ', c.reason], ['আটক স্থান', c.location.label], ['আটক তারিখ', fmtDate(c.createdAt)], ['ডাম্পিং স্টেশন', (c.station?.station ?? c.assignedStation).replace(' ডাম্পিং স্টেশন', '')], ['জরিমানা', taka(c.fine + c.extraCharge)]].map(([k, v]) => <div key={k} className="flex justify-between gap-3 px-4 py-2" data-id="element-1549" data-mp="3cewwmv1ehsc8"><dt className="text-ink-muted" data-id="element-1550" data-mp="3h6x02xgoj3dk">{k}</dt><dd className="text-right font-semibold" data-id="element-1551" data-mp="3h7zq7366h1sf">{v}</dd></div>)}
            <div className="flex items-center justify-between gap-3 px-4 py-2.5" data-id="element-1552" data-mp="32ik07peh10ds"><dt className="text-ink-muted" data-id="element-1553" data-mp="37gc4liwayeyv">বর্তমান অবস্থা</dt><dd data-id="element-1554" data-mp="37h51zt9dfs4c"><StatusBadge label={s.label} tone={s.tone} size="sm" data-id="element-1555" data-mp="37h50fotq3k28" /></dd></div>
          </dl>
        </div>

        <div className="card p-4" data-id="element-1556" data-mp="3v6zda6e1l8mh">
          <p className="mb-3 text-sm font-bold text-ink" data-id="element-1557" data-mp="3zmwhsh2mx73">প্রক্রিয়ার অগ্রগতি</p>
          <ProcessTracker c={c} compact data-id="element-1558" data-mp="3xivbiak4cai" />
        </div>

        {c.stage !== 'released' && c.docStatus === 'none' && <div className={`rounded-xl p-3 text-sm ${daysLeft <= 2 ? 'bg-danger-50 text-danger-700' : 'bg-warning-50 text-warning-700'}`} data-id="element-1559" data-mp="3q6tk2ls4je2a">
            কাগজপত্র জমার শেষ তারিখ <b data-id="element-1560" data-mp="2ap1kv3kdn6f5">{fmtDate(c.deadline)}</b> — আর <b data-id="element-1561" data-mp="2erof1b4it3fi">{bn(daysLeft)} দিন</b> বাকি।
          </div>}
        {correction.length > 0 && <div className="rounded-xl border border-danger/30 bg-danger-50 p-3 text-sm text-danger-700" data-id="element-1562" data-mp="2785g1vevszla">
            <p className="flex items-center gap-1.5 font-bold" data-id="element-1563" data-mp="229u4ng8xuh0y"><AlertTriangleIcon className="h-4 w-4" data-id="element-1564" data-mp="2292jz0gvq3pg" /> সংশোধন প্রয়োজন</p>
            {correction.map(d => <p key={d.key} className="mt-1 text-xs" data-id="element-1565" data-mp="2h5tk5bydnjg4"><b data-id="element-1566" data-mp="2c7rqxy78m3qx">{d.name}:</b> {d.reason}</p>)}
          </div>}
      </div>
      <div className="shrink-0 border-t border-line bg-white p-3 grid grid-cols-2 gap-2" data-id="element-1567" data-mp="2c5nozlswfeta">
        <button className="btn btn-outline" onClick={() => go({
        name: 'documents',
        pcr
      })} data-id="element-1568" data-mp="1ofte39s540bo"><UploadIcon className="h-4 w-4" data-id="element-1569" data-mp="1jkdyacaknucm" /> কাগজপত্র</button>
        <button className={`btn ${isPaid(c) ? 'btn-outline' : 'btn-green'}`} onClick={() => go({
        name: 'payment',
        pcr
      })} data-id="element-1570" data-mp="1y88rdtshs0q8"><CreditCardIcon className="h-4 w-4" data-id="element-1571" data-mp="1tfz8uytpi2xz" /> {isPaid(c) ? 'পেমেন্ট রসিদ' : 'জরিমানা পরিশোধ'}</button>
        <button className="btn btn-outline" onClick={() => go({
        name: 'progress',
        pcr
      })} data-id="element-1572" data-mp="15qeeljs3rj9e"><ListChecksIcon className="h-4 w-4" data-id="element-1573" data-mp="10suwu9w74ocp" /> অগ্রগতি</button>
        <button className="btn btn-outline" onClick={() => go({
        name: 'letter',
        pcr
      })} data-id="element-1574" data-mp="10qij68xh105g"><FileCheck2Icon className="h-4 w-4" data-id="element-1575" data-mp="42sw19k1ixne" /> অবমুক্তি পত্র</button>
      </div>
    </>;
}